import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Observable, Subscriber, defer } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { OdooJsonRequest } from './odoo-json-request';
import { ConnectionService } from './connection.service';
import { LoggerService } from '../logger/logger.service';
import { environment } from '../../../environments/environment';
import { LoadingSpinnerService } from '../loading-spinner.service';
import { OdooException } from './odoo-exception';

@Injectable()
export class ConnectionOdooService extends ConnectionService {

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private loadingSpinnerService: LoadingSpinnerService,
  ) {
    super();
  }

  sendGetRequest(url: string, params?: any): Observable<ArrayBuffer> {
    return this.sendRequest(url, params, 'get');
  }

  sendMethodRequest(url: string, params?: any): Observable<any> {
    const odooJsonRequest = new OdooJsonRequest(params);
    odooJsonRequest.method = 'call';
    return this.sendJsonPostRequestInternal(url, odooJsonRequest);
  }

  sendJsonPostRequest(url: string, params?: any): Observable<any> {
    return this.sendJsonPostRequestInternal(url, new OdooJsonRequest(params));
  }

  private sendJsonPostRequestInternal(url: string, odooJsonRequest: OdooJsonRequest): Observable<any> {
    return defer(() => {
      this.loadingSpinnerService.show();
      return this.sendRequest(url, odooJsonRequest, 'post')
        .pipe(
          map(
            (data: any) => {
              if (data && data.error) {
                const odooException = OdooException.createFromAny(data.error);
                this._odooErrorHandling(odooException);
                throw odooException || data.error;
              } else if (data && data.result) {
                return data.result;
              } else {
                return null;
              }
            }),
          catchError(
            error => {
              let errorMessage = error || '';
              if (error && error.error) {
                errorMessage = error.error;
              }

              if (error && error.message) {
                errorMessage = error.message;
              }

              this.logger.error(`ConnectionOdooService: sendJsonPostRequestInternal error: "${errorMessage}."`);

              throw error;
            }
          ),
          finalize(
            () => {
              this.loadingSpinnerService.hide();
            }
          ),
        );
    });
  }

  private _odooErrorHandling(odooException: OdooException) {
    if (odooException && odooException.isSessionExpired) {
      this.clearAuthorizationToken();
      this.authorizationTokenExpired.emit();
    }
  }

  private sendRequest(url: string, params: any, type: string): Observable<ArrayBuffer> {
    const fullUrl = this.buildFullUrl(url);

    let headers = new HttpHeaders();

    if (this.authorizationToken) {
      headers = headers.set('X-Openerp-Session-Id', this.authorizationToken);
    }

    const requestOptions: any = { headers };

    this.logger.info(`ConnectionOdooService: sendRequest ${type} ${url}`);

    if (type === 'post' || type === 'form') {
      if (type === 'post') {
        headers = headers.set('Content-Type', 'application/json');
      }
      return this.http.post(fullUrl, params, requestOptions);
    } else {
      requestOptions.params = params;
      return this.http.get(fullUrl, requestOptions);
    }
  }

  private get baseUrl(): string {
    return environment.odooUrl || '';
  }

  private buildFullUrl(url: string): string {
    return `${this.baseUrl}${url}`;
  }

  sendFormPostRequest(url: string, formData?: FormData): Observable<any> {
    return this.sendRequest(url, formData, 'form');
  }
}
